import { useEffect } from 'react';
import { Outlet, createRootRouteWithContext, useRouterState } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import type { RouterContext } from '../router.tsx';
import { AppShell } from '../components/layout/AppShell.tsx';
import { useFilterStore } from '../stores/filter-store.ts';
import { parseRootSearch, type RootSearch } from '../lib/url-state.ts';
import { CommandPalette } from '../features/search/CommandPalette.tsx';
import { CFAnalytics } from '../components/CFAnalytics.tsx';

/**
 * Root route — owns the layout shell and search-param schema for `/`.
 *
 * Why search lives at the root:
 *   the URL is the source of truth for `team / city / country / program /
 *   division / event` (PRD §7.2.5). Child routes inherit, so deep-linking
 *   `/?team=839A` works whether the user is on the homepage globe or a
 *   detail page.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  validateSearch: (raw): RootSearch => parseRootSearch(raw as Record<string, unknown>),
  component: RootComponent,
});

function RootComponent() {
  const search = useRouterState({ select: (s) => s.location.search as RootSearch });
  const hydrate = useFilterStore((s) => s.hydrate);

  // Mirror URL search → filter store on every navigation. The store never
  // writes back here; navigation paths use `router.navigate({ search })`.
  useEffect(() => {
    hydrate(search);
  }, [search, hydrate]);

  return (
    <>
      <Helmet>
        <title>VEX Teams Atlas</title>
        <meta
          name="description"
          content="A global interactive atlas of VEX Robotics teams, stories, and championship journeys."
        />
        <meta property="og:title" content="VEX Teams Atlas" />
        <meta
          property="og:description"
          content="Explore where VEX teams come from."
        />
      </Helmet>
      <CFAnalytics />
      <AppShell>
        <Outlet />
      </AppShell>
      <CommandPalette />
    </>
  );
}
